import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { ReviewsList } from '../Reviews/ReviewsList';

export const UserProfile = () => {
  const { userId } = useParams();
  const { user, profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [viewProfile, setViewProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ displayName: '', grade: '', subjectsGoodAt: [], subjectsNeedHelp: [], contactWhatsApp: '', contactEmail: '' });
  const subjects = ['Math', 'English', 'Science', 'History', 'Geography', 'Physics', 'Chemistry', 'Biology', 'Chinese', 'French', 'Economics', 'Business', 'Computer Science', 'Other'];

  useEffect(() => { fetchProfile(); }, [userId]);

  const fetchProfile = async () => {
    try {
      if (userId && userId !== user.uid) {
        const docRef = doc(db, 'users', userId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          data.uid = snap.id;
          setViewProfile(data);
        }
      } else { 
        const data = {...profile, uid: user.uid};
        setViewProfile(data); 
      }
    } catch (err) { console.error('Error:', err); } finally { setLoading(false); }
  };

  const handleEdit = () => {
    setFormData({ displayName: profile.displayName || '', grade: profile.grade || '', subjectsGoodAt: profile.subjectsGoodAt || [], subjectsNeedHelp: profile.subjectsNeedHelp || [], contactWhatsApp: profile.contactWhatsApp || '', contactEmail: profile.contactEmail || '' });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try { await updateProfile(formData); setIsEditing(false); alert('Profile updated!'); } catch (err) { alert('Error: ' + err.message); }
  };

  const toggleSubject = (field, subj) => {
    setFormData(prev => { const list = prev[field]; return { ...prev, [field]: list.includes(subj) ? list.filter(s => s !== subj) : [...list, subj] }; });
  };

  if (loading) return <p style={{textAlign:'center', padding:'2rem'}}>Loading profile...</p>;
  const displayProfile = viewProfile || profile;
  const isOwnProfile = !userId || userId === user.uid;
  const profileUid = displayProfile.uid || userId || user.uid;

  return (
    <div className="profile-page">
      <Card className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">{displayProfile.displayName?.charAt(0).toUpperCase() || 'U'}</div>
          <div className="profile-info">
            <h2>{displayProfile.displayName || 'User'}</h2>
            <p className="profile-email">{displayProfile.email}</p>
            <div className="badges">
              {displayProfile.isTeacher && <span className="teacher-badge">Teacher</span>}
              {displayProfile.isTopTutor && <span className="top-tutor-badge">🏆 Top Tutor</span>}
            </div>
          </div>
        </div>
        <div className="profile-stats">
          <div className="stat-box"><span className="stat-value">⭐ {displayProfile.points || 0}</span><span className="stat-label">Points</span></div>
          <div className="stat-box"><span className="stat-value">🌟 {displayProfile.rating?.toFixed(1) || '0.0'}</span><span className="stat-label">Rating</span></div>
          <div className="stat-box"><span className="stat-value">{displayProfile.grade || 'N/A'}</span><span className="stat-label">Grade</span></div>
        </div>
        {isOwnProfile && !isEditing && <Button onClick={handleEdit} className="btn-primary">✏️ Edit Profile</Button>}
        {isEditing && (
          <div className="edit-form">
            <div className="form-group"><label>Display Name</label><input type="text" value={formData.displayName} onChange={e => setFormData({...formData, displayName: e.target.value})} /></div>
            <div className="form-group"><label>Grade</label><select value={formData.grade} onChange={e => setFormData({...formData, grade: e.target.value})}><option value="">Select Grade</option>{['G7','G8','G9','G10','G11','G12'].map(g => <option key={g} value={g}>{g}</option>)}</select></div>
            <div className="form-group"><label>Subjects I'm Good At</label><div className="subject-tags">{subjects.map(s => <button key={s} type="button" className={formData.subjectsGoodAt.includes(s) ? 'tag active' : 'tag'} onClick={() => toggleSubject('subjectsGoodAt', s)}>{s}</button>)}</div></div>
            <div className="form-group"><label>WhatsApp</label><input type="text" value={formData.contactWhatsApp} onChange={e => setFormData({...formData, contactWhatsApp: e.target.value})} placeholder="+852 XXXX XXXX" /></div>
            <div className="btn-group"><Button onClick={() => setIsEditing(false)} className="btn-outline">Cancel</Button><Button onClick={handleSave} className="btn-primary">Save Changes</Button></div>
          </div>
        )}
        {!isEditing && (
          <div className="profile-details">
            <div className="detail-section">
              <h3> Subjects I'm Good At</h3>
              <div className="subject-list">
                {displayProfile.subjectsGoodAt?.length > 0 ? displayProfile.subjectsGoodAt.map(s => <span key={s} className="subject-tag">{s}</span>) : <p className="no-data">Not specified</p>}
              </div>
            </div>
            {isOwnProfile && displayProfile.contactWhatsApp && (
              <div className="contact-section">
                <h3>📞 Contact</h3>
                <p><strong>WhatsApp:</strong> {displayProfile.contactWhatsApp}</p>
                <a href={`https://wa.me/${displayProfile.contactWhatsApp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">💬 Chat on WhatsApp</a>
              </div>
            )}
            <ReviewsList userId={profileUid} currentUserId={user.uid} />
          </div>
        )}
      </Card>
    </div>
  );
};
